import { useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { Button, Link, makeStyles, MessageBar, MessageBarBody, tokens } from '@fluentui/react-components';
import { CheckmarkRegular, MailRegular, PersonAvailableRegular } from '@fluentui/react-icons';
import { Shell, ContactFooter } from '../components/Shell';
import { IconCircle, IconList, Lead, Muted, Title } from '../components/ui';
import { isSignedIn, signOut } from '../lib/msAuth';
import { useSignup } from '../lib/signupState';

const useStyles = makeStyles({
  actions: { display: 'grid', gap: '12px' },
  primary: { minHeight: '48px', fontSize: tokens.fontSizeBase400 },
  secondary: { minHeight: '44px' },
});

/**
 * After the details are saved (at the table or from the QR card): finish now,
 * or leave it and use the emailed link later.
 */
export function Saved() {
  const s = useStyles();
  const navigate = useNavigate();
  const { state, reset } = useSignup();
  const [later, setLater] = useState(false);

  if (!state.details || !state.captured) return <Navigate to="/details" replace />;
  const first = state.details.name.split(/\s+/)[0];

  // A shared tablet at the table: forget this person (and any Microsoft sign-in) for the next one.
  async function nextPerson() {
    reset();
    if (await isSignedIn().catch(() => false)) await signOut('/');
    else navigate('/');
  }

  return (
    <Shell
      labelledBy="title"
      brandTitle="Saved"
      brandTagline="Finish now if you have two minutes and a Global Admin to hand, or from your desk after the event."
      footerLeft={<ContactFooter />}
    >
      <IconCircle tone="ok">
        <CheckmarkRegular />
      </IconCircle>
      <Title id="title">You're in, {first}.</Title>

      {later ? (
        <>
          <Lead>
            We'll email {state.details.email} a personal link. It opens with your details filled in, so finishing takes
            about two minutes.
          </Lead>
          <MessageBar intent="success">
            <MessageBarBody>All saved. You can close this page.</MessageBarBody>
          </MessageBar>
        </>
      ) : (
        <>
          <Lead>Your details are saved. Finish now, or we'll email you a link to finish from your desk.</Lead>
          <IconList
            label="To finish"
            items={[
              { icon: <CheckmarkRegular />, title: 'Agree to the NFR terms', text: 'One page; we email you a PDF copy.' },
              {
                icon: <PersonAvailableRegular />,
                title: 'Approve access in Microsoft',
                text: "Needs a Global Admin for your tenant. Not you? You can send them the link.",
              },
              { icon: <MailRegular />, title: 'Findings the next morning', text: 'Your first scan runs that night.' },
            ]}
          />
          <div className={s.actions}>
            <Button appearance="primary" size="large" className={s.primary} onClick={() => navigate('/terms')}>
              Finish now
            </Button>
            <Button size="large" className={s.secondary} onClick={() => setLater(true)}>
              I'll finish later
            </Button>
          </div>
        </>
      )}

      <Muted>
        Using TELLEC's device? <Link onClick={nextPerson}>Start a new sign-up</Link> for the next person.
      </Muted>
    </Shell>
  );
}
